import React, {useEffect, useRef, useState} from "react";
import s from "../wiki/Wiki.module.css";
import w from "./Widget.module.css";

const WIDGET_URL = "https://my.unlimco.io";

const CopyBtn = ({getText, title = "Скопировать"}) => {
    const [ok, setOk] = useState(false);
    return (
        <button
            type="button"
            className={s.copyBtn}
            title={title}
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(getText());
                    setOk(true);
                    setTimeout(() => setOk(false), 1200);
                } catch (e) {
                    console.error("Ошибка копирования:", e);
                }
            }}
        >
            {ok ? "✓" : "⧉"}
        </button>
    );
};

const Code = ({lang = "html", children}) => {
    const pre = useRef(null);
    const getText = () => (pre.current ? pre.current.innerText : "");
    return (
        <div className={s.code}>
            <div className={s.codeBar}>
                <span className={s.codeLang}>{lang}</span>
                <CopyBtn getText={getText}/>
            </div>
            <pre ref={pre}>
                <code>{children}</code>
            </pre>
        </div>
    );
};

const Kbd = ({children}) => <kbd className={s.kbd}>{children}</kbd>;

const WidgetDocs = () => {
    const [demoWidg] = useState("1245");
    const [demoUnlid] = useState("demo-user-123");

    // Добавляем класс на body для мобильной адаптации только этой страницы
    useEffect(() => {
        document.body.classList.add("widget-page");

        // Добавляем стили для мобильной адаптации
        const styleId = "widget-mobile-styles";
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = styleId;
            styleElement.textContent = `
                @media (max-width: 640px) {
                    body.widget-page {
                        overflow-x: hidden !important;
                    }
                    body.widget-page > div > div {
                        min-width: auto !important;
                    }
                    body.widget-page header > div,
                    body.widget-page footer > div,
                    body.widget-page > div > div > div,
                    body.widget-page main {
                        width: 100% !important;
                        max-width: 100% !important;
                        padding: 0 12px !important;
                        box-sizing: border-box !important;
                    }
                    body.widget-page footer span {
                        font-size: 12px !important;
                    }
                }
            `;
            document.head.appendChild(styleElement);
        }

        return () => {
            document.body.classList.remove("widget-page");
            const el = document.getElementById(styleId);
            if (el) {
                el.remove();
            }
        };
    }, []);

    // Загружаем скрипт виджета для демо
    useEffect(() => {
        const existingScript = document.querySelector('script[src*="widget.js"]');
        if (!existingScript) {
            const script = document.createElement("script");
            script.src = WIDGET_URL + "/widget.js";
            script.async = true;
            document.body.appendChild(script);
        }
    }, []);

    return (
        <div className={s.page}>
            <main className={s.container}>
                {/* Hero */}
                <section className={s.hero}>
                    <h1 className={s.h1}>Виджет оплаты</h1>
                    <p className={s.lead}>
                        Встраиваемая платёжная форма для вашего сайта. Позволяет удобно принимать
                        платежи без
                        сложной интеграции — достаточно добавить одну строку кода.
                    </p>
                    <div className={s.meta}>
                        <span>
                            Скрипт: <code>{WIDGET_URL}/widget.js</code>
                        </span>
                        <span>
                            Метод: <code>iframe</code>
                        </span>
                    </div>
                </section>

                {/* Как подключить */}
                <section className={s.card}>
                    <h2 className={s.h2}>Как подключить</h2>
                    <ol className={s.ol}>
                        <li>
                            <strong>Свяжитесь с менеджером</strong> — напишите вашему
                            обслуживающему менеджеру и сообщите:
                            <ul style={{marginTop: 8, marginLeft: 20}}>
                                <li>URL вашего сайта, где будет размещён виджет</li>
                                <li>
                                    <Kbd>callbackUrl</Kbd> — адрес для получения уведомлений
                                    о статусе платежей (webhook)
                                </li>
                            </ul>
                        </li>
                        <li>
                            <strong>Получите ключ виджета</strong> — менеджер выдаст вам
                            уникальный <Kbd>widg</Kbd> (ID виджета), который привязан к
                            вашему мерчант-аккаунту и настройкам.
                        </li>
                        <li>
                            <strong>Добавьте скрипт на сайт</strong> — вставьте код перед
                            закрывающим тегом <code>&lt;/body&gt;</code>
                        </li>
                        <li>
                            <strong>Добавьте кнопку оплаты</strong> — создайте элемент с
                            атрибутами <Kbd>widg</Kbd> и <Kbd>unlid</Kbd>
                        </li>
                    </ol>
                </section>

                {/* Быстрый старт */}
                <section className={s.card}>
                    <h2 className={s.h2}>Быстрый старт</h2>

                    <h3 className={s.h3}>1. Подключите скрипт</h3>
                    <p className={s.note}>
                        Добавьте скрипт виджета перед закрывающим тегом <code>&lt;/body&gt;</code>
                    </p>
                    <Code lang="html">{`<script src="${WIDGET_URL}/widget.js"></script>`}</Code>

                    <h3 className={s.h3} style={{marginTop: 24}}>2. Добавьте кнопку оплаты</h3>
                    <p className={s.note}>
                        Создайте кнопку (или любой другой элемент) с атрибутами <Kbd>widg</Kbd> и <Kbd>unlid</Kbd>
                    </p>
                    <Code lang="html">{`<button widg="ВАШ_КЛЮЧ_ВИДЖЕТА" unlid="ID_ЗАКАЗА_ИЛИ_ПОЛЬЗОВАТЕЛЯ">
    Оплатить
</button>`}</Code>

                    <div className={s.note} style={{marginTop: 16}}>
                        <strong>Готово!</strong> При клике на кнопку откроется модальное окно
                        с формой оплаты.
                    </div>
                </section>

                {/* Параметры */}
                <section className={s.card}>
                    <h2 className={s.h2}>Параметры</h2>
                    <div className={w.paramsTable}>
                        <div className={w.paramRow}>
                            <div className={w.paramName}><code>widg</code></div>
                            <div className={w.paramDesc}>
                                <strong>Обязательный.</strong> ID виджета, выданный менеджером.
                                Привязан к вашему мерчант-аккаунту и определяет настройки
                                приёма платежей (комиссии, лимиты, callback URL).
                            </div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}><code>unlid</code></div>
                            <div className={w.paramDesc}>
                                <strong>Обязательный.</strong> Ваш внутренний идентификатор —
                                например, ID заказа, ID пользователя или любая строка для
                                связи платежа с вашей системой. Передаётся в callback и
                                доступен при проверке статуса.
                            </div>
                        </div>
                    </div>
                </section>

                {/* Демо */}
                <section className={s.card}>
                    <h2 className={s.h2}>Демонстрация</h2>
                    <p className={s.note}>
                        Нажмите на кнопку, чтобы увидеть виджет в действии. Это реальная
                        форма оплаты с тестовыми параметрами.
                    </p>

                    <div className={w.demoArea}>
                        <button
                            className={w.demoButton}
                            widg={demoWidg}
                            unlid={demoUnlid}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="1" y="4" width="22" height="16" rx="2"/>
                                <line x1="1" y1="10" x2="23" y2="10"/>
                            </svg>
                            Оплатить заказ
                        </button>
                        <div className={w.demoParams}>
                            <span><code>widg="{demoWidg}"</code></span>
                            <span><code>unlid="{demoUnlid}"</code></span>
                        </div>
                    </div>

                    <h3 className={s.h3} style={{marginTop: 24}}>Код этой кнопки</h3>
                    <Code lang="html">{`<button widg="${demoWidg}" unlid="${demoUnlid}">
    Оплатить заказ
</button>`}</Code>
                </section>

                {/* Программный вызов */}
                <section className={s.card}>
                    <h2 className={s.h2}>Программный вызов (JavaScript API)</h2>
                    <p className={s.note}>
                        Помимо автоматической привязки к элементам с атрибутами, вы можете
                        открывать виджет программно через JavaScript.
                    </p>

                    <h3 className={s.h3}>Открыть виджет</h3>
                    <Code lang="javascript">{`UnlimcoWidget.open({
    widg: 'ВАШ_КЛЮЧ_ВИДЖЕТА',
    unlid: 'ID_ЗАКАЗА_ИЛИ_ПОЛЬЗОВАТЕЛЯ'
});`}</Code>

                    <h3 className={s.h3} style={{marginTop: 16}}>Закрыть виджет</h3>
                    <Code lang="javascript">{`UnlimcoWidget.close();`}</Code>

                    <h3 className={s.h3} style={{marginTop: 16}}>Пример использования</h3>
                    <Code lang="html">{`<button onclick="openPayment()">Оплатить через JS</button>

<script src="${WIDGET_URL}/widget.js"></script>
<script>
function openPayment() {
    // Получаем ID заказа из вашей системы
    const orderId = document.getElementById('order-id').value;

    UnlimcoWidget.open({
        widg: 'ВАШ_КЛЮЧ_ВИДЖЕТА',
        unlid: 'order-' + orderId
    });
}
</script>`}</Code>
                </section>

                {/* Callback (Webhook) */}
                <section className={s.card}>
                    <h2 className={s.h2}>Уведомления о платежах (Callback)</h2>
                    <p className={s.note}>
                        После завершения платежа (успешного или неуспешного) на ваш
                        <Kbd>callbackUrl</Kbd> будет отправлен POST-запрос с информацией
                        о транзакции.
                    </p>

                    <h3 className={s.h3}>Формат callback</h3>
                    <Code lang="json">{`{
  "transactionId": 2316512,
  "externalReference": null,
  "state": "COMPLETED",
  "amount": 5000,
  "currency": "RUB",
  "processingId": "5849a1b2-c3d4-5e6f-7890-abcdef123456",
  "createdAt": "2025-12-17T14:30:00+00:00",
  "unlid": "order-12345"
}`}</Code>

                    <h3 className={s.h3} style={{marginTop: 16}}>Возможные значения state</h3>
                    <div className={w.paramsTable}>
                        <div className={w.paramRow}>
                            <div className={w.paramName}><code>COMPLETED</code></div>
                            <div className={w.paramDesc}>
                                Платёж успешно завершён. Деньги получены.
                            </div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}><code>DECLINED</code></div>
                            <div className={w.paramDesc}>
                                Платёж отклонён (недостаточно средств, ошибка банка и т.д.)
                            </div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}><code>CANCELED</code></div>
                            <div className={w.paramDesc}>
                                Платёж отменён пользователем или истёк срок ожидания.
                            </div>
                        </div>
                    </div>

                    <div className={s.note} style={{marginTop: 16}}>
                        <strong>Важно:</strong> Callback отправляется с повторными попытками
                        при ошибках сети или 5xx ответах. Ваш сервер должен возвращать
                        HTTP 200 для подтверждения получения.
                    </div>
                </section>

                {/* Проверка статуса */}
                <section className={s.card}>
                    <h2 className={s.h2}>Проверка статуса транзакции</h2>
                    <p className={s.note}>
                        Вы можете проверить статус платежа вручную через API. Это полезно,
                        если callback не дошёл или нужно перепроверить состояние.
                    </p>

                    <h3 className={s.h3}>Запрос по transactionId</h3>
                    <Code lang="bash">{`curl -X GET 'https://api.unlimco.io/api/hydra/status/{transactionId}' \\
  -H 'X-Merchant-UUID: ВАШ_UUID' \\
  -H 'X-Merchant-Key: ВАШ_KEY'`}</Code>

                    <h3 className={s.h3} style={{marginTop: 16}}>Запрос по unlid</h3>
                    <p className={s.note}>
                        Можно также искать транзакцию по вашему внутреннему идентификатору <Kbd>unlid</Kbd>
                    </p>
                    <Code lang="bash">{`curl -X GET 'https://api.unlimco.io/api/hydra/status/{unlid}' \\
  -H 'X-Merchant-UUID: ВАШ_UUID' \\
  -H 'X-Merchant-Key: ВАШ_KEY'`}</Code>

                    <h3 className={s.h3} style={{marginTop: 16}}>Пример ответа</h3>
                    <Code lang="json">{`{
  "transactionId": 2316512,
  "externalReference": null,
  "amount": 5000,
  "currency": "RUB",
  "processingId": "5849a1b2-c3d4-5e6f-7890-abcdef123456",
  "hydraStatus": "success",
  "hydraState": "COMPLETED",
  "synced": true,
  "createdAt": "2025-12-17T14:30:00+00:00",
  "unlid": "order-12345"
}`}</Code>

                    <div className={s.note} style={{marginTop: 16}}>
                        <strong>Примечание:</strong> Для получения <Kbd>X-Merchant-UUID</Kbd> и
                        <Kbd>X-Merchant-Key</Kbd> обратитесь к вашему менеджеру.
                    </div>
                </section>

                {/* Полный пример */}
                <section className={s.card}>
                    <h2 className={s.h2}>Полный пример интеграции</h2>
                    <Code lang="html">{`<!DOCTYPE html>
<html lang="ru">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Мой магазин — Оплата</title>
    <style>
        .pay-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;   
            padding: 16px 32px;
            font-size: 16px;
            font-weight: 600;
            border-radius: 12px;
            cursor: pointer;
        }
        .pay-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
        }
    </style>
</head>
<body>
    <h1>Оформление заказа #12345</h1>
    <p>Сумма к оплате: <strong>5 000 ₽</strong></p>

    <!-- Способ 1: Кнопка с атрибутами -->
    <button class="pay-btn" widg="ВАШ_КЛЮЧ" unlid="order-12345">
        Оплатить 5 000 ₽
    </button>

    <!-- Способ 2: Программный вызов -->
    <button class="pay-btn" onclick="payOrder()">
        Оплатить через JS
    </button>

    <!-- Подключаем виджет -->
    <script src="${WIDGET_URL}/widget.js"></script>
    <script>
        function payOrder() {
            UnlimcoWidget.open({
                widg: 'ВАШ_КЛЮЧ',
                unlid: 'order-12345'
            });
        }
    </script>
</body>
</html>`}</Code>
                </section>

                {/* Лимиты и ограничения */}
                <section className={s.card}>
                    <h2 className={s.h2}>Лимиты и ограничения</h2>
                    <div className={w.paramsTable}>
                        <div className={w.paramRow}>
                            <div className={w.paramName}>Минимальная сумма</div>
                            <div className={w.paramDesc}><strong>1 000 ₽</strong></div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}>Максимальная сумма</div>
                            <div className={w.paramDesc}><strong>75 000 ₽</strong></div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}>Валюта</div>
                            <div className={w.paramDesc}><strong>RUB</strong> (российский рубль)</div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}>Время жизни платежа</div>
                            <div className={w.paramDesc}><strong>30 минут</strong> с момента создания</div>
                        </div>
                        <div className={w.paramRow}>
                            <div className={w.paramName}>Поддерживаемые банки</div>
                            <div className={w.paramDesc}><strong>T-Bank</strong> (Тинькофф)</div>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className={s.card}>
                    <h2 className={s.h2}>Частые вопросы</h2>

                    <h3 className={s.h3}>Как получить ключ виджета?</h3>
                    <p className={s.note}>
                        Напишите вашему менеджеру и сообщите URL сайта и адрес для callback.
                        После настройки вы получите уникальный <code>widg</code>.
                    </p>

                    <h3 className={s.h3} style={{marginTop: 16}}>Можно ли изменить внешний вид виджета?</h3>
                    <p className={s.note}>
                        Модальное окно виджета имеет фиксированный дизайн для обеспечения
                        узнаваемости и доверия пользователей. Стили кнопки на вашем сайте
                        вы определяете сами.
                    </p>

                    <h3 className={s.h3} style={{marginTop: 16}}>Как тестировать интеграцию?</h3>
                    <p className={s.note}>
                        Запросите у менеджера тестовый ключ виджета. Тестовые платежи
                        не будут списывать реальные деньги.
                    </p>

                    <h3 className={s.h3} style={{marginTop: 16}}>Что делать, если callback не приходит?</h3>
                    <p className={s.note}>
                        Проверьте, что ваш сервер доступен из интернета и возвращает HTTP 200.
                        Используйте ручную проверку статуса через API. Если проблема
                        сохраняется — обратитесь в поддержку.
                    </p>
                </section>

                {/* Поддержка */}
                <section className={s.card}>
                    <h2 className={s.h2}>Поддержка</h2>
                    <p className={s.note}>
                        По вопросам интеграции и техническим проблемам обращайтесь к
                        вашему персональному менеджеру или в техническую поддержку.
                    </p>
                </section>

                <footer className={s.footer}>
                    <span>© unlimco</span>
                    <span className={s.dot}>•</span>
                    <a className={s.link} href="#top">
                        Наверх
                    </a>
                </footer>
            </main>
        </div>
    );
};

export default WidgetDocs;



// WEBPACK FOOTER //
// ./src/PagesOfCommon/Widget/index.jsx